import { createAgreementMutation, validateAgreementOptions, validateAgreementQueryKey } from '@helios/frontend-api-v2-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useUser } from '../../api/user/useUser';
import { getFriendlyError } from '../../utils/error';

export interface UseTermsAgreement {
  isValidTerm?: boolean;
  isValidateFetched: boolean;
  isValidateLoading: boolean;
  isPostLoading: boolean;
  showTermsAgreement: boolean;
  validateError?: string;
  createTermsAgreement: () => void;
}

export const useTermsAgreement = (termName: string): UseTermsAgreement => {
  const { isAuthenticated, isImpersonatedUser } = useUser();
  const queryClient = useQueryClient();

  const {
    mutate,
    isPending: isPostLoading,
    error: postError,
  } = useMutation({
    ...createAgreementMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: validateAgreementQueryKey({ path: { termName } }) }),
  });

  const {
    data: validation,
    isFetched: isValidateFetched,
    isPending: isValidateLoading,
    error: validateError,
  } = useQuery({
    ...validateAgreementOptions({ path: { termName } }),
    enabled: isAuthenticated && Boolean(termName),
  });

  const isValidTerm = validation?.agreed;

  return {
    createTermsAgreement: () => mutate({ body: { termName } }),
    isValidTerm,
    isValidateFetched,
    isValidateLoading,
    isPostLoading,
    showTermsAgreement: isValidateFetched && !isValidTerm && !validateError && !isImpersonatedUser(),
    validateError: getFriendlyError(postError || validateError, 'terms agreement'),
  };
};

export default useTermsAgreement;
